import React from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  Breadcrumbs,
  Link,
  Divider
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import GavelIcon from '@mui/icons-material/Gavel';
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PeopleIcon from '@mui/icons-material/People';
import Footer from '../Footer/Footer';

const CommunityGuidelines: React.FC = () => {
  const lastUpdated = "April 17, 2025";

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumbs navigation */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link
          component={RouterLink}
          to="/"
          sx={{ display: 'flex', alignItems: 'center', color: 'black' }}
        >
          <HomeIcon sx={{ mr: 0.5, }} fontSize="inherit" />
          Home
        </Link>
        <Link
          component={RouterLink}
          to="/terms"
          sx={{ display: 'flex', alignItems: 'center', color: 'black' }}
        >
          <GavelIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Terms of Service
        </Link>
        <Link
          component={RouterLink}
          to="/privacy"
          sx={{ display: 'flex', alignItems: 'center', color: 'black' }}
        >
          <PrivacyTipIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Privacy Policy
        </Link>
        <Link
          component={RouterLink}
          to="/privacy"
          sx={{ display: 'flex', alignItems: 'center',}}
        >
          <PeopleIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Community Guidelines
        </Link>
      </Breadcrumbs>

      <Paper elevation={0} sx={{ p: 4, borderRadius: 2, mb: 6 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Community Guidelines
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Last Updated: {lastUpdated}
          </Typography>
        </Box>

        <Typography paragraph>
          Welcome to UnitedInTalk. These Community Guidelines are designed to ensure our platform remains a respectful, 
          productive space for Americans of all backgrounds to engage in meaningful dialogue. By using our platform, 
          you agree to follow these guidelines.
        </Typography>

        <Box sx={{ my: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            1. Our Mission
          </Typography>
          <Typography paragraph>
            UnitedInTalk exists to foster respectful conversations across political divides. We believe that through 
            direct dialogue, Americans can better understand each other's perspectives and find common ground despite 
            differences.
          </Typography>
        </Box>

        <Box sx={{ my: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            2. Respectful Communication
          </Typography>
          <Typography paragraph>
            All members are expected to communicate with respect, even when disagreeing. This means:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>Listen actively to others' viewpoints without interrupting</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>Focus on ideas rather than attacking individuals</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>Use appropriate language and avoid inflammatory rhetoric</Typography>
            </Box>
            <Box component="li">
              <Typography>Acknowledge that reasonable people can hold different views</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ my: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            3. Prohibited Content
          </Typography>
          <Typography paragraph>
            The following types of content are not allowed on UnitedInTalk:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography><strong>Hate speech:</strong> Content that promotes hatred, discrimination, or violence against individuals or groups based on attributes such as race, ethnicity, religious affiliation, disability, gender, age, veteran status, or sexual orientation</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography><strong>Harassment:</strong> Bullying, intimidation, or targeted abuse of individuals or groups</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography><strong>Misinformation:</strong> Deliberate spreading of false information that could cause harm</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography><strong>Personal information:</strong> Sharing others' private information without consent</Typography>
            </Box>
            <Box component="li">
              <Typography><strong>Illegal content:</strong> Content that violates applicable laws or regulations</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ my: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            4. Moderation
          </Typography>
          <Typography paragraph>
            Our moderation approach is designed to maintain a healthy community while respecting different viewpoints:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>We do not moderate based on political viewpoint or ideology</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>We may intervene when conversations violate these guidelines</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>Users may be warned, temporarily restricted, or permanently banned depending on the severity and frequency of violations</Typography>
            </Box>
            <Box component="li">
              <Typography>Appeals can be submitted for review by our moderation team</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ my: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            5. Reporting Violations
          </Typography>
          <Typography paragraph>
            If you encounter content that violates these guidelines:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>Email team@arczen.com with your email address, room ID of the reported call, and the name of the user you are reporting (if possible)</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>Provide context to help our moderators understand the issue</Typography>
            </Box>
            <Box component="li">
              <Typography>For serious issues, contact us directly at team@arczen.com</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ my: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            6. Conversation Best Practices
          </Typography>
          <Typography paragraph>
            To get the most out of your experience:
          </Typography>
          <Box component="ul" sx={{ pl: 4 }}>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>Enter conversations with an open mind</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>Ask questions to better understand others' perspectives</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>Share personal experiences rather than making broad generalizations</Typography>
            </Box>
            <Box component="li" sx={{ mb: 1 }}>
              <Typography>Look for common ground and shared values</Typography>
            </Box>
            <Box component="li">
              <Typography>Take breaks when conversations become heated</Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ my: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            7. Updates to Guidelines
          </Typography>
          <Typography paragraph>
            These guidelines may be updated from time to time. We will notify users of significant changes and the 
            current version will always be available at unitedintalk.com/guidelines.
          </Typography>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="subtitle1">
            By using UnitedInTalk, you acknowledge that you have read, understood, and agree to follow these community guidelines.
          </Typography>
        </Box>
      </Paper>
      
      <Footer />
    </Container>
  );
};

export default CommunityGuidelines;